import React, { useState } from "react";
import {
  beautyAddServiceApi,
  beautyUpdateProfessionalApi,
  beautyUpdateServiceApi,
} from "../../api/organizerBeautyApi";
import AvisoSenasBloqueadas from "../components/AvisoSenasBloqueadas";
import OrganizerPillSelect from "../components/OrganizerPillSelect";
import { Dinero } from "../canchas/primerUso/clubPasos";
import { Campo } from "../reservas/primerUso/barPasos";
import { faltaServicios, nuevoServicio, payloadServicio } from "./primerUso/salonPayload";
import { idDe } from "./profesionalPayload";
import { AdicionalesSection, AvisoConVariantes, VariantesBox } from "./ServicioOpciones";
import {
  adicionalesDe,
  faltaAdicionales,
  faltaVariantes,
  masBarata,
  nuevaVariante,
  payloadAdicionales,
  payloadVariantes,
  variantesDe,
} from "./opcionesDeServicio";
import "./turnos-beauty.css";
import "../events/EventForm.css";
import "../primerUso/primerUso.css";
import "../reservas/reservas-organizer.css";

/** Minutes kept busy after the service, for cleaning or preparing. Same set the back accepts. */
export const OPCIONES_PREPARACION = [
  { value: "0", label: "Sin tiempo extra" },
  { value: "5", label: "5 minutos" },
  { value: "10", label: "10 minutos" },
  { value: "15", label: "15 minutos" },
  { value: "20", label: "20 minutos" },
  { value: "30", label: "30 minutos" },
];

const plata = (n) => `$ ${Number(n || 0).toLocaleString("es-AR")}`;

const opcionesDe = (s) => {
  const variantes = variantesDe(s);
  return { conVariantes: variantes.length > 0, variantes, adicionales: adicionalesDe(s) };
};

const formDe = (s) =>
  s
    ? {
        ...opcionesDe(s),
        nombre: s.name || "",
        categoria: s.category || "",
        descripcion: s.description || "",
        duracion: String(s.durationMinutes ?? "60"),
        precio: String(s.price ?? ""),
        sena: s.deposit ? String(s.deposit) : "",
        preparacion: String(s.bufferAfterMinutes || 0),
        activo: s.active !== false,
      }
    : { ...nuevoServicio(), ...opcionesDe(null), descripcion: "", preparacion: "0", activo: true };

/** Side panel to create / edit a service. `servicio` null = new. Deposits follow the same lock as the guided flow.
 * Rendered as a modal over the services table.
 * `acciones` (optional node) renders secondary actions (reorder, delete) at the bottom of the modal. */
const ServicioForm = ({
  servicio,
  categorias = [],
  senasBloqueadas = false,
  profesionales = [],
  acciones = null,
  onClose,
  onSaved,
}) => {
  const [f, setF] = useState(() => formDe(servicio));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Professional overrides state: map proId -> { enabled, precio, duracion, sena, touched }
  const [proOverrides, setProOverrides] = useState(() => {
    if (!profesionales.length) return {};
    const map = {};
    const sId = servicio ? String(servicio._id) : "";
    profesionales.forEach((p) => {
      const pid = idDe(p);
      if (!servicio) {
        // New service: every active professional does it by default (saved with the service).
        const activa = p.active !== false;
        map[pid] = { enabled: activa, precio: "", duracion: "", sena: "", personalizando: false, touched: activa };
        return;
      }
      const serviceIds = (p.serviceIds || p.services || []).map(idDe);
      const performs = serviceIds.includes(sId);
      const o = (p.serviceOverrides || []).find((ov) => idDe(ov.serviceId) === sId);
      map[pid] = {
        enabled: performs,
        precio: o?.price != null ? String(o.price) : "",
        duracion: o?.durationMinutes != null ? String(o.durationMinutes) : "",
        sena: o?.deposit != null ? String(o.deposit) : "",
        personalizando: false,
        touched: false,
      };
    });
    return map;
  });

  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

  const setProState = (pid, patch) => {
    setProOverrides((prev) => ({
      ...prev,
      [pid]: { ...prev[pid], ...patch, touched: true },
    }));
  };

  // With variants the service price and duration come from the cheapest one (the back derives them too).
  const barata = f.conVariantes ? masBarata(f.variantes) : null;

  const guardar = async () => {
    const faltaOpciones =
      (f.conVariantes ? faltaVariantes(f.variantes, senasBloqueadas ? "" : f.sena) : "") || faltaAdicionales(f.adicionales);
    // Without variants the base fields are checked as always; with them, the cheapest variant stands in for them.
    const base = barata ? { ...f, duracion: barata.duracion, precio: barata.precio, sena: "" } : f;
    const falta = faltaOpciones || faltaServicios({ servicios: [base] });
    if (falta) {
      setError(falta);
      return;
    }
    const body = {
      ...payloadServicio(barata ? { ...f, duracion: barata.duracion, precio: barata.precio } : f),
      active: f.activo,
      description: (f.descripcion || "").trim(),
      bufferAfterMinutes: Number(f.preparacion) || 0,
    };
    // Full lists, keeping the _id of existing entries. A service that never had options sends neither field.
    if (f.conVariantes || servicio?.variants?.length) body.variants = f.conVariantes ? payloadVariantes(f.variantes) : [];
    if (f.adicionales.length || servicio?.addOns?.length) body.addOns = payloadAdicionales(f.adicionales);
    if (servicio) body.category = (f.categoria || "").trim();
    // Without validated payments the back rejects any deposit; an untouched one is simply not re-sent.
    if (senasBloqueadas) delete body.deposit;
    setError("");
    setSaving(true);
    try {
      let savedServiceId = servicio?._id;
      if (servicio) {
        await beautyUpdateServiceApi({ id: servicio._id, ...body });
      } else {
        const res = await beautyAddServiceApi(body);
        savedServiceId = res?.data?.info?._id;
      }

      // If any professional overrides or assignments were touched and we have the service ID, sync them
      if (savedServiceId && Object.values(proOverrides).some((po) => po.touched)) {
        const sId = String(savedServiceId);
        await Promise.all(
          profesionales.map(async (p) => {
            const pid = idDe(p);
            const po = proOverrides[pid];
            if (!po || !po.touched) return;

            let nextServiceIds = (p.serviceIds || p.services || []).map(idDe);
            if (po.enabled && !nextServiceIds.includes(sId)) {
              nextServiceIds = [...nextServiceIds, sId];
            } else if (!po.enabled && nextServiceIds.includes(sId)) {
              nextServiceIds = nextServiceIds.filter((id) => id !== sId);
            }

            let nextOverrides = (p.serviceOverrides || []).filter((o) => idDe(o.serviceId) !== sId);
            if (po.enabled && (po.precio || po.duracion || po.sena)) {
              nextOverrides.push({
                serviceId: sId,
                price: po.precio ? Number(po.precio) : null,
                durationMinutes: po.duracion ? Number(po.duracion) : null,
                deposit: po.sena ? Number(po.sena) : null,
              });
            }

            return beautyUpdateProfessionalApi({
              id: p._id,
              name: p.name,
              active: p.active !== false,
              serviceIds: nextServiceIds,
              serviceOverrides: nextOverrides,
              weeklySchedule: p.weeklySchedule || [],
            });
          })
        );
      }

      onSaved(servicio ? "Servicio actualizado" : "Servicio creado");
    } catch (e) {
      setError(e?.response?.data?.message || "No pudimos guardar el servicio. Probá de nuevo.");
      setSaving(false);
    }
  };

  const titulo = servicio ? "Editar servicio" : "Nuevo servicio";

  return (
    <div className="zr-overlay" onMouseDown={saving ? undefined : onClose}>
    <div
      className="zr-modal zr-side-panel zr-srv-modal"
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="zr-side-panel__head">
        <h3 className="zr-side-panel__title">{titulo}</h3>
        <button type="button" className="zr-btn zr-btn--ghost zr-side-panel__close" onClick={onClose} aria-label="Cerrar">
          ×
        </button>
      </div>

        <div className="pu-body">
          {error && <div className="ef-alert" role="alert">{error}</div>}

          <Campo label="Nombre">
            <input
              className="ef-input"
              aria-label="Nombre del servicio"
              value={f.nombre}
              onChange={(e) => set({ nombre: e.target.value })}
              placeholder="Ej: Corte mujer"
            />
          </Campo>

          <Campo label="Categoría (opcional)">
            <input
              className="ef-input"
              aria-label="Categoría"
              list="turnos-categorias"
              value={f.categoria}
              onChange={(e) => set({ categoria: e.target.value })}
              placeholder="Ej: Cortes, Color, Tratamientos..."
            />
            <datalist id="turnos-categorias">
              {categorias.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </Campo>

          <Campo label="Descripción (opcional)">
            <textarea
              className="ef-textarea"
              aria-label="Descripción"
              style={{ height: 70 }}
              value={f.descripcion}
              onChange={(e) => set({ descripcion: e.target.value })}
              placeholder="Detalle o recomendaciones para los clientes..."
            />
          </Campo>

          <VariantesBox
            activo={f.conVariantes}
            onToggle={(conVariantes) =>
              set({
                conVariantes,
                variantes: conVariantes && !f.variantes.length ? [nuevaVariante({ duracion: f.duracion, precio: f.precio })] : f.variantes,
              })
            }
            variantes={f.variantes}
            onChange={(variantes) => set({ variantes })}
            precioDesde={barata ? Number(barata.precio) : 0}
          />

          {!f.conVariantes && (
          <div className="pu-grid-2">
            <Campo label="Duración (min)" hint="De a 5 minutos.">
              <input
                className="ef-input"
                type="number"
                min="5"
                step="5"
                aria-label="Duración (min)"
                value={f.duracion}
                onChange={(e) => set({ duracion: e.target.value })}
              />
            </Campo>
            <Campo label="Precio">
              <Dinero label="Precio" value={f.precio} onChange={(v) => set({ precio: v })} />
            </Campo>
          </div>
          )}

          {senasBloqueadas && <AvisoSenasBloqueadas />}
          <div className="pu-grid-2">
            <Campo
              label="Seña (opcional)"
              hint={`Se cobra al reservar cuando tus cobros están validados.${f.conVariantes ? " No puede superar la variante más barata." : ""}`}
            >
              <Dinero
                label="Seña"
                value={f.sena}
                placeholder="Sin seña"
                disabled={senasBloqueadas}
                onChange={(v) => set({ sena: v })}
              />
            </Campo>
            <div className="ef-field">
              <span className="ef-label" id="srv-preparacion-label">Preparación después</span>
              <OrganizerPillSelect
                id="srv-preparacion"
                labelledBy="srv-preparacion-label"
                value={f.preparacion}
                onChange={(val) => set({ preparacion: String(val) })}
                options={OPCIONES_PREPARACION}
              />
              <p className="ef-footnote">Se bloquea en la agenda al terminar, para limpiar o preparar. El cliente no lo ve y no se cobra.</p>
            </div>
          </div>

          <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
            <input
              type="checkbox"
              className="zr-switch"
              aria-label="Servicio activo"
              checked={f.activo}
              onChange={(e) => set({ activo: e.target.checked })}
            />
            Activo (se puede reservar)
          </label>

          <AdicionalesSection adicionales={f.adicionales} onChange={(adicionales) => set({ adicionales })} />

          {/* Sección prototipo: Precio y duración por profesional */}
          {profesionales.length > 0 && (
            <div className="zr-overrides-section">
              <h4 className="zr-overrides-title">Precio y duración por profesional</h4>
              <p className="zr-overrides-hint">
                Si alguien cobra distinto o tarda más, cargalo acá. No hace falta repetir el servicio.
              </p>
              {f.conVariantes && <AvisoConVariantes />}

              {profesionales.map((p) => {
                const pid = idDe(p);
                const po = proOverrides[pid] || {
                  enabled: false,
                  precio: "",
                  duracion: "",
                  sena: "",
                  personalizando: false,
                };
                const inicial = (p.name || "P").charAt(0).toUpperCase();
                // With variants every professional uses the variants' prices and durations: only "Lo realiza" applies.
                const tienePropio = !f.conVariantes && Boolean(po.precio || po.duracion || po.sena);

                return (
                  <div key={pid} className="zr-pro-override-row">
                    <div className="zr-pro-override-info">
                      <span className="zr-avatar zr-avatar--sm zr-avatar--c1">{inicial}</span>
                      <div>
                        <div className="zr-pro-override-name">{p.name}</div>
                        <div className="zr-pro-override-status">
                          {!po.enabled ? (
                            "No lo realiza"
                          ) : f.conVariantes ? (
                            "Lo realiza"
                          ) : tienePropio ? (
                            <>
                              {po.duracion ? `${po.duracion} min` : `${f.duracion || 60} min`} ·{" "}
                              {po.precio ? plata(po.precio) : plata(f.precio)}
                            </>
                          ) : (
                            `Usa el base · ${f.duracion || 60} min · ${plata(f.precio)}`
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bt-ov-actions" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {tienePropio && <span className="zr-pro-override-badge">Propio</span>}
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          className="zr-switch"
                          aria-label={`${p.name} realiza ${f.nombre || "servicio"}`}
                          checked={po.enabled}
                          onChange={(e) => setProState(pid, { enabled: e.target.checked })}
                        />
                      </label>
                      {po.enabled && !f.conVariantes && (
                        <button
                          type="button"
                          className="zr-btn zr-btn--ghost"
                          style={{ fontSize: 12, padding: "4px 8px" }}
                          aria-label={`${tienePropio ? "Editar" : "Personalizar"} ${p.name}`}
                          onClick={() => setProState(pid, { personalizando: !po.personalizando })}
                        >
                          {tienePropio ? "Editar" : "Personalizar"}
                        </button>
                      )}
                      {po.enabled && !f.conVariantes && (tienePropio || po.personalizando) && (
                        <button
                          type="button"
                          className="zr-btn zr-btn--ghost"
                          style={{ fontSize: 12, padding: "4px 8px" }}
                          aria-label={`Quitar precio propio de ${p.name}`}
                          onClick={() => setProState(pid, { precio: "", duracion: "", sena: "", personalizando: false })}
                        >
                          Quitar
                        </button>
                      )}
                    </div>

                    {po.enabled && po.personalizando && !f.conVariantes && (
                      <div
                        className="bt-ov-grid"
                        style={{
                          width: "100%",
                          paddingTop: 8,
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 8,
                        }}
                      >
                        <Campo label="Precio propio" hint={`Base: ${plata(f.precio)}`}>
                          <Dinero
                            label={`Precio propio de ${p.name}`}
                            value={po.precio}
                            placeholder={String(f.precio || 0)}
                            onChange={(v) => setProState(pid, { precio: v })}
                          />
                        </Campo>
                        <Campo label="Duración propia (min)" hint={`Base: ${f.duracion} min`}>
                          <input
                            className="ef-input"
                            type="number"
                            min="5"
                            step="5"
                            aria-label={`Duración propia de ${p.name}`}
                            value={po.duracion}
                            placeholder={`${f.duracion} min`}
                            onChange={(e) => setProState(pid, { duracion: e.target.value })}
                          />
                        </Campo>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

      {acciones && <div className="zr-side-actions">{acciones}</div>}

      <div className="zr-side-panel__foot">
        <button type="button" className="zr-btn zr-btn--ghost" onClick={onClose} disabled={saving}>
          Cancelar
        </button>
        <button type="button" className="zr-btn zr-btn--dark" onClick={guardar} disabled={saving}>
          {saving ? "Guardando..." : "Guardar cambios"}
        </button>
      </div>
    </div>
    </div>
  );
};

export default ServicioForm;
